/**
 * FeatureUsageAlert — iter-169z3.
 *
 * Compact info chip rendered on the dashboard that warns the user when
 * any metered feature is close to running out. Looks at
 * `/billing/me → usage.feature_usage[]` (already powering MyPlan) and
 * surfaces the lowest-remaining item with a short call to action.
 *
 * Hidden when:
 *   • No metered features on the plan.
 *   • All features sit above the 20% remaining threshold.
 *   • The user dismisses it (per-feature, persisted in localStorage so
 *     it doesn't nag every session).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShoppingCart, Sparkles, X } from "lucide-react";
import { api } from "../../lib/api";

const LOW_THRESHOLD = 0.2; // surface when remaining < 20%

// Single source-of-truth label map. Mirrors `MyPlan.jsx` so renaming
// stays cheap.
const LABELS = {
  image_extractions: { es: "recibos IA", en: "AI receipts" },
  advisor_chat:      { es: "consultas IA", en: "AI advisor messages" },
};

function labelFor(key, lang) {
  const m = LABELS[key];
  if (!m) return key;
  return m[lang] || m.es;
}

function dismissKey(featureKey, periodKey) {
  return `lumfi.feature-usage-dismiss.${featureKey}.${periodKey || "current"}`;
}

export function FeatureUsageAlert({ lang = "es" }) {
  const { data: billing } = useQuery({
    queryKey: ["billing-me"],
    queryFn: async () => (await api.get("/billing/me")).data,
    staleTime: 60_000,
    retry: false,
  });

  // Pick the most-pressing low-usage feature (smallest remaining ratio).
  const lowest = useMemo(() => {
    const items = billing?.usage?.feature_usage || [];
    let best = null;
    for (const it of items) {
      const allot = Number(it.allotment) || 0;
      if (allot <= 0) continue;
      const remaining = Math.max(0, Number(it.remaining ?? (allot - (it.used || 0))));
      const ratio = remaining / allot;
      if (ratio >= LOW_THRESHOLD) continue;
      if (!best || ratio < best.ratio) {
        best = { ...it, remaining, allot, ratio };
      }
    }
    return best;
  }, [billing]);

  const periodKey = billing?.usage?.period_key;
  const dKey = lowest ? dismissKey(lowest.feature_key, periodKey) : null;
  // Track dismissals at the (feature_key, period_key) granularity so the
  // chip stops nagging once the user already saw + acted on it this
  // billing period. A simple version counter lets us "wake up" the chip
  // when billing data refreshes (new period_key → new dKey → unread).
  const [dismissVersion, setDismissVersion] = useState(0);
  const dismissed = useMemo(() => {
    if (!dKey || typeof window === "undefined") return false;
    return window.localStorage.getItem(dKey) === "1";
  }, [dKey, dismissVersion]);

  if (!lowest || dismissed) return null;
  const isEs = lang !== "en";
  const label = labelFor(lowest.feature_key, lang);
  const isLast = lowest.remaining <= 1;
  const tone = isLast
    ? "bg-rose-50 border-rose-300 text-rose-900"
    : "bg-amber-50 border-amber-300 text-amber-900";
  const iconTone = isLast ? "text-rose-600" : "text-amber-600";

  return (
    <div
      className={`rounded-2xl border ${tone} px-4 py-3 flex items-center gap-3 mb-4`}
      data-testid={`feature-usage-alert-${lowest.feature_key}`}
    >
      <Sparkles className={`h-4 w-4 shrink-0 ${iconTone}`} />
      <div className="flex-1 text-sm leading-snug">
        <strong>
          {isEs
            ? `Te quedan ${lowest.remaining} ${label} este mes.`
            : `${lowest.remaining} ${label} left this month.`}
        </strong>{" "}
        <span className="opacity-75">
          {isEs ? "Compra un pack para no quedarte sin." : "Buy a pack to keep going."}
        </span>
      </div>
      <Link to="/pricing">
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          className="rounded-lg bg-white/80 border border-current/40 hover:bg-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5"
          data-testid={`feature-usage-alert-${lowest.feature_key}-cta`}
        >
          <ShoppingCart className="h-3 w-3" />
          {isEs ? "Comprar pack" : "Buy pack"}
        </button>
      </Link>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={() => {
          if (dKey) window.localStorage.setItem(dKey, "1");
          setDismissVersion((v) => v + 1);
        }}
        className="text-current/60 hover:text-current"
        data-testid={`feature-usage-alert-${lowest.feature_key}-dismiss`}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
